import React, { useEffect, useState, useCallback } from 'react';
import { getContracts, getCompanies, getAssets, updatePO, mobilizeContract, uploadFile, uploadClientPO, getDocuments, createDocument } from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Receipt, FileText, Truck, CheckCircle2, AlertCircle, Search, Filter, Upload, Eye, Calendar, Building2, DollarSign, FileUp, X, Download, Clock, Circle, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

// Contract Timeline Component
const ContractTimeline = ({ contract }) => {
  const stages = [
    { 
      key: 'created', 
      label: 'Created', 
      date: contract.created_at,
      icon: Circle,
      color: 'text-blue-500',
      bgColor: 'bg-blue-100',
      completed: true
    },
    { 
      key: 'pending_po', 
      label: 'Pending PO', 
      date: contract.created_at,
      icon: AlertCircle,
      color: 'text-amber-500',
      bgColor: 'bg-amber-100',
      completed: ['pending_po', 'po_received', 'mobilization', 'active', 'completed'].includes(contract.status)
    },
    { 
      key: 'po_received', 
      label: 'PO Received', 
      date: contract.po_received_date,
      icon: FileText,
      color: 'text-indigo-500',
      bgColor: 'bg-indigo-100',
      completed: ['po_received', 'mobilization', 'active', 'completed'].includes(contract.status)
    },
    { 
      key: 'mobilization', 
      label: 'Mobilized', 
      date: contract.mobilization_date,
      icon: Truck,
      color: 'text-purple-500',
      bgColor: 'bg-purple-100',
      completed: ['mobilization', 'active', 'completed'].includes(contract.status)
    },
    { 
      key: 'active', 
      label: 'Active', 
      date: contract.mobilization_date,
      icon: CheckCircle2,
      color: 'text-green-500',
      bgColor: 'bg-green-100',
      completed: ['active', 'completed'].includes(contract.status)
    },
    { 
      key: 'completed', 
      label: 'Completed', 
      date: contract.completed_date,
      icon: CheckCircle2,
      color: 'text-gray-500',
      bgColor: 'bg-gray-100',
      completed: contract.status === 'completed'
    }
  ];

  // Calculate days between stages
  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Find current stage index
  const currentStageIndex = stages.findIndex(s => s.key === contract.status);
  
  // Calculate total duration
  const totalDuration = calculateDuration(contract.created_at, contract.status === 'completed' ? contract.completed_date : new Date().toISOString());

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 text-center">
          <p className="text-xs text-muted-foreground">Current Stage</p>
          <p className="font-bold text-lg capitalize">{contract.status?.replace('_', ' ')}</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-xs text-muted-foreground">Days Active</p>
          <p className="font-bold text-lg">{totalDuration || 0}</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-xs text-muted-foreground">Progress</p>
          <p className="font-bold text-lg">{Math.round(((currentStageIndex + 1) / stages.length) * 100)}%</p>
        </Card>
      </div>

      {/* Timeline */}
      <div className="relative">
        {stages.map((stage, index) => {
          const Icon = stage.icon;
          const isActive = stage.key === contract.status;
          const isPast = stage.completed && !isActive;
          const isFuture = !stage.completed;
          
          // Duration to next stage
          const nextStage = stages[index + 1];
          const durationToNext = nextStage?.date && stage.date 
            ? calculateDuration(stage.date, nextStage.date) 
            : null;
          
          return (
            <div key={stage.key} className="flex items-start mb-0 last:mb-0">
              {/* Icon and Line */}
              <div className="flex flex-col items-center mr-4">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center
                  ${isActive ? `${stage.bgColor} ring-4 ring-offset-2 ring-${stage.color.split('-')[1]}-200` : ''}
                  ${isPast ? stage.bgColor : ''}
                  ${isFuture ? 'bg-gray-100' : ''}
                  transition-all duration-300
                `}>
                  <Icon className={`h-5 w-5 ${isFuture ? 'text-gray-300' : stage.color}`} />
                </div>
                {index < stages.length - 1 && (
                  <div className={`w-0.5 h-16 ${isPast || isActive ? 'bg-green-300' : 'bg-gray-200'}`} />
                )}
              </div>
              
              {/* Content */}
              <div className="flex-1 pb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className={`font-semibold ${isFuture ? 'text-gray-400' : ''}`}>
                      {stage.label}
                      {isActive && (
                        <Badge className="ml-2 bg-green-100 text-green-800 text-xs">Current</Badge>
                      )}
                    </h4>
                    {stage.date && !isFuture && (
                      <p className="text-sm text-muted-foreground">
                        {new Date(stage.date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    )}
                    {isFuture && (
                      <p className="text-sm text-gray-400">Pending</p>
                    )}
                  </div>
                  
                  {/* Duration badge */}
                  {durationToNext && isPast && (
                    <Badge variant="outline" className="text-xs">
                      {durationToNext} day{durationToNext !== 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
                
                {/* Additional info for specific stages */}
                {stage.key === 'po_received' && contract.po_number && isPast && (
                  <p className="text-xs text-muted-foreground mt-1">
                    PO: {contract.po_number}
                  </p>
                )}
                {stage.key === 'active' && contract.assigned_assets?.length > 0 && (isPast || isActive) && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {contract.assigned_assets.length} asset(s) mobilized
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottleneck Warning */}
      {contract.status === 'pending_po' && totalDuration > 7 && (
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            <div>
              <p className="font-semibold text-amber-800">Potential Bottleneck Detected</p>
              <p className="text-sm text-amber-700">
                Contract has been pending PO for {totalDuration} days. Consider following up with the client.
              </p>
            </div>
          </div>
        </Card>
      )}
      
      {contract.status === 'po_received' && !contract.mobilization_date && (
        <Card className="p-4 bg-blue-50 border-blue-200">
          <div className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-blue-600" />
            <div>
              <p className="font-semibold text-blue-800">Ready for Mobilization</p>
              <p className="text-sm text-blue-700">
                PO received. Assets can now be assigned and mobilized.
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

const statusConfig = {
  pending_po: { color: 'bg-amber-100 text-amber-800', label: 'Pending PO', icon: AlertCircle },
  po_received: { color: 'bg-blue-100 text-blue-800', label: 'PO Received', icon: FileText },
  mobilization: { color: 'bg-purple-100 text-purple-800', label: 'Mobilization', icon: Truck },
  active: { color: 'bg-green-100 text-green-800', label: 'Active', icon: CheckCircle2 },
  completed: { color: 'bg-gray-100 text-gray-800', label: 'Completed', icon: CheckCircle2 },
  cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled', icon: AlertCircle },
};

export const ContractsPage = () => {
  const [contracts, setContracts] = useState([]);
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  
  // Dialogs
  const [poDialogOpen, setPoDialogOpen] = useState(false);
  const [mobilizeDialogOpen, setMobilizeDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  
  // State
  const [selectedContract, setSelectedContract] = useState(null);
  const [poNumber, setPoNumber] = useState('');
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [contractDocuments, setContractDocuments] = useState([]);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    await Promise.all([loadCompanies(), loadContracts()]);
  };

  const loadCompanies = async () => {
    try {
      const response = await getCompanies();
      setCompanies(response.data);
    } catch (error) {
      console.error('Failed to load companies:', error);
    }
  };

  const loadContracts = async () => {
    setLoading(true);
    try {
      const response = await getContracts();
      setContracts(response.data);
      setFilteredContracts(response.data);
    } catch (error) {
      toast.error('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableAssets = async (companyId) => {
    try {
      const response = await getAssets(companyId, 'available');
      setAssets(response.data);
    } catch (error) {
      console.error('Failed to load assets:', error);
    }
  };

  const loadContractDocuments = async (contractId) => {
    try {
      const response = await getDocuments('contract', contractId);
      setContractDocuments(response.data);
    } catch (error) {
      console.error('Failed to load documents:', error);
      setContractDocuments([]);
    }
  };

  // Apply filters
  const applyFilters = useCallback(() => {
    let result = [...contracts];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(c => 
        c.contract_number?.toLowerCase().includes(query) ||
        c.client_name?.toLowerCase().includes(query) ||
        c.po_number?.toLowerCase().includes(query)
      );
    }
    
    if (statusFilter !== 'all') {
      result = result.filter(c => c.status === statusFilter);
    }
    
    if (companyFilter !== 'all') {
      result = result.filter(c => c.company_id === companyFilter);
    }
    
    setFilteredContracts(result);
  }, [contracts, searchQuery, statusFilter, companyFilter]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleUpdatePO = async () => {
    if (!poNumber) {
      toast.error('Please enter PO number');
      return;
    }
    try {
      await updatePO(selectedContract.id, poNumber);
      toast.success('PO number updated!');
      setPoDialogOpen(false);
      setPoNumber('');
      loadContracts();
    } catch (error) {
      toast.error('Failed to update PO');
    }
  };

  const handleMobilize = async () => {
    if (selectedAssets.length === 0) {
      toast.error('Please select at least one asset');
      return;
    }
    try {
      await mobilizeContract(selectedContract.id, selectedAssets);
      toast.success('Contract mobilized successfully!');
      setMobilizeDialogOpen(false);
      setSelectedAssets([]);
      loadContracts();
    } catch (error) {
      toast.error('Failed to mobilize contract');
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    
    setUploadingFile(true);
    try {
      // Upload the file
      const uploadResponse = await uploadFile(selectedFile);
      const fileUrl = uploadResponse.data.file_url;
      
      // Attach to contract
      await uploadClientPO(selectedContract.id, fileUrl);
      
      // Also create a document record
      await createDocument({
        document_type: 'client_po',
        reference_type: 'contract',
        reference_id: selectedContract.id,
        reference_name: selectedContract.contract_number,
        expiry_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
        file_url: fileUrl,
        notes: `Client PO for ${selectedContract.contract_number}`
      });
      
      toast.success('Client PO uploaded successfully!');
      setUploadDialogOpen(false);
      setSelectedFile(null);
      loadContracts();
    } catch (error) {
      toast.error('Failed to upload file');
      console.error(error);
    } finally {
      setUploadingFile(false);
    }
  };

  const openDetailsDialog = (contract) => {
    setSelectedContract(contract);
    loadContractDocuments(contract.id);
    setDetailsDialogOpen(true);
  };

  const openPODialog = (contract) => {
    setSelectedContract(contract);
    setPoDialogOpen(true);
  };

  const openMobilizeDialog = (contract) => {
    setSelectedContract(contract);
    loadAvailableAssets(contract.company_id);
    setMobilizeDialogOpen(true);
  };

  const openUploadDialog = (contract) => {
    setSelectedContract(contract);
    setUploadDialogOpen(true);
  };

  const toggleAsset = (assetId) => {
    setSelectedAssets(prev => 
      prev.includes(assetId) ? prev.filter(id => id !== assetId) : [...prev, assetId]
    );
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || 'Unknown';
  };

  const getCompanyCurrency = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.currency || 'SAR';
  };

  // Statistics
  const stats = {
    total: contracts.length,
    active: contracts.filter(c => c.status === 'active').length,
    pendingPO: contracts.filter(c => c.status === 'pending_po').length,
    totalValue: contracts.reduce((sum, c) => sum + (c.contract_value || 0), 0)
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black tracking-tight" data-testid="contracts-title">Contracts</h1>
          <p className="text-muted-foreground mt-1">{filteredContracts.length} of {contracts.length} contracts</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Receipt className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Contracts</p>
              <p className="text-2xl font-bold">{stats.total}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Active</p>
              <p className="text-2xl font-bold">{stats.active}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Clock className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Pending PO</p>
              <p className="text-2xl font-bold">{stats.pendingPO}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <DollarSign className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Value</p>
              <p className="text-2xl font-bold">{stats.totalValue.toLocaleString()}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by contract number, client, or PO..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-contracts"
            />
          </div>
          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]" data-testid="status-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending_po">Pending PO</SelectItem>
                <SelectItem value="po_received">PO Received</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger className="w-[180px]" data-testid="company-filter">
                <Building2 className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Company" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companies.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Contracts Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading contracts...</div>
        </div>
      ) : filteredContracts.length === 0 ? (
        <Card className="p-12 text-center">
          <Receipt className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No contracts found</h3>
          <p className="text-muted-foreground">
            {contracts.length === 0 
              ? 'Contracts are created from accepted quotations'
              : 'Try adjusting your search or filters'}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredContracts.map((contract) => {
            const StatusIcon = statusConfig[contract.status]?.icon || Receipt;
            
            return (
              <Card key={contract.id} className="p-6 hover:shadow-lg transition-shadow duration-200" data-testid={`contract-card-${contract.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-accent/10 rounded-lg">
                      <Receipt className="h-6 w-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-bold" data-testid={`contract-number-${contract.id}`}>{contract.contract_number}</h3>
                      <p className="text-sm text-muted-foreground">{contract.client_name}</p>
                    </div>
                  </div>
                  <Badge className={statusConfig[contract.status]?.color} data-testid={`contract-status-${contract.id}`}>
                    <StatusIcon className="h-3 w-3 mr-1" />
                    {statusConfig[contract.status]?.label}
                  </Badge>
                </div>

                <div className="space-y-2 text-sm mb-4">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Contract Value:</span>
                    <span className="font-bold">{contract.contract_value?.toLocaleString()} {getCompanyCurrency(contract.company_id)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Company:</span>
                    <span className="text-xs">{getCompanyName(contract.company_id)}</span>
                  </div>
                  {contract.po_number && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">PO Number:</span>
                      <span className="font-mono text-xs">{contract.po_number}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Start Date:</span>
                    <span>{new Date(contract.start_date).toLocaleDateString()}</span>
                  </div>
                  {contract.assigned_assets && contract.assigned_assets.length > 0 && (
                    <div className="pt-2 border-t">
                      <div className="flex items-center gap-1">
                        <Truck className="h-4 w-4 text-accent" />
                        <span className="text-sm font-semibold">{contract.assigned_assets.length} assets assigned</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex gap-2 flex-wrap">
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => openDetailsDialog(contract)}
                    data-testid={`view-details-${contract.id}`}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    Details
                  </Button>
                  
                  {contract.status === 'pending_po' && (
                    <>
                      <Button 
                        size="sm" 
                        className="bg-accent hover:bg-accent/90" 
                        onClick={() => openPODialog(contract)}
                        data-testid={`update-po-${contract.id}`}
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Add PO
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => openUploadDialog(contract)}
                        data-testid={`upload-po-${contract.id}`}
                      >
                        <Upload className="h-4 w-4 mr-1" />
                        Upload PO
                      </Button>
                    </>
                  )}
                  
                  {contract.status === 'po_received' && (
                    <Button 
                      size="sm" 
                      className="bg-green-600 hover:bg-green-700" 
                      onClick={() => openMobilizeDialog(contract)}
                      data-testid={`mobilize-${contract.id}`}
                    >
                      <Truck className="h-4 w-4 mr-1" />
                      Mobilize
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Contract Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Contract Details - {selectedContract?.contract_number}
            </DialogTitle>
            <DialogDescription>
              View contract information and documents
            </DialogDescription>
          </DialogHeader>
          
          {selectedContract && (
            <Tabs defaultValue="details" className="mt-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="timeline">Timeline</TabsTrigger>
                <TabsTrigger value="documents">Documents</TabsTrigger>
              </TabsList>
              
              <TabsContent value="details" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div>
                      <Label className="text-muted-foreground">Contract Number</Label>
                      <p className="font-semibold">{selectedContract.contract_number}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Client Name</Label>
                      <p className="font-semibold">{selectedContract.client_name}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Status</Label>
                      <Badge className={statusConfig[selectedContract.status]?.color}>
                        {statusConfig[selectedContract.status]?.label}
                      </Badge>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Contract Value</Label>
                      <p className="font-semibold text-lg">
                        {selectedContract.contract_value?.toLocaleString()} {getCompanyCurrency(selectedContract.company_id)}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-muted-foreground">Company</Label>
                      <p className="font-semibold">{getCompanyName(selectedContract.company_id)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">PO Number</Label>
                      <p className="font-mono">{selectedContract.po_number || 'Not received'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Start Date</Label>
                      <p>{new Date(selectedContract.start_date).toLocaleDateString()}</p>
                    </div>
                    {selectedContract.end_date && (
                      <div>
                        <Label className="text-muted-foreground">End Date</Label>
                        <p>{new Date(selectedContract.end_date).toLocaleDateString()}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <Label className="text-muted-foreground">Payment Terms</Label>
                  <p>{selectedContract.payment_terms || 'Net 30'}</p>
                </div>
                
                {selectedContract.assigned_assets?.length > 0 && (
                  <div className="pt-4 border-t">
                    <Label className="text-muted-foreground">Assigned Assets</Label>
                    <div className="flex items-center gap-2 mt-2">
                      <Truck className="h-5 w-5 text-green-600" />
                      <span className="font-semibold">{selectedContract.assigned_assets.length} vehicles mobilized</span>
                    </div>
                  </div>
                )}
                
                {selectedContract.notes && (
                  <div className="pt-4 border-t">
                    <Label className="text-muted-foreground">Notes</Label>
                    <p className="text-sm">{selectedContract.notes}</p>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="timeline" className="mt-4">
                <ContractTimeline contract={selectedContract} />
              </TabsContent>
              
              <TabsContent value="documents" className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">Contract documents and attachments</p>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={() => {
                      setDetailsDialogOpen(false);
                      openUploadDialog(selectedContract);
                    }}
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Document
                  </Button>
                </div>
                
                {selectedContract.client_po_url && (
                  <Card className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <FileText className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-semibold">Client Purchase Order</p>
                          <p className="text-xs text-muted-foreground">Uploaded by client</p>
                        </div>
                      </div>
                      <a 
                        href={`${process.env.REACT_APP_BACKEND_URL}${selectedContract.client_po_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent hover:underline flex items-center gap-1"
                      >
                        <Download className="h-4 w-4" />
                        Download
                      </a>
                    </div>
                  </Card>
                )}
                
                {selectedContract.documents?.map((doc, index) => (
                  <Card key={index} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-100 rounded-lg">
                          <FileText className="h-5 w-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="font-semibold capitalize">{doc.type?.replace('_', ' ')}</p>
                          <p className="text-xs text-muted-foreground">
                            {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'Unknown date'}
                          </p>
                        </div>
                      </div>
                      {doc.url && (
                        <a 
                          href={`${process.env.REACT_APP_BACKEND_URL}${doc.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent hover:underline flex items-center gap-1"
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </a>
                      )}
                    </div>
                  </Card>
                ))}
                
                {contractDocuments?.map((doc) => (
                  <Card key={doc.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-100 rounded-lg">
                          <FileText className="h-5 w-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="font-semibold capitalize">{doc.document_type?.replace('_', ' ')}</p>
                          <p className="text-xs text-muted-foreground">{doc.notes}</p>
                        </div>
                      </div>
                      {doc.file_url && (
                        <a 
                          href={`${process.env.REACT_APP_BACKEND_URL}${doc.file_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent hover:underline flex items-center gap-1"
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </a>
                      )}
                    </div>
                  </Card>
                ))}
                
                {!selectedContract.client_po_url && 
                 !selectedContract.documents?.length && 
                 !contractDocuments?.length && (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No documents uploaded yet</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>

      {/* PO Number Dialog */}
      <Dialog open={poDialogOpen} onOpenChange={setPoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Purchase Order Number</DialogTitle>
            <DialogDescription>
              Enter the client's PO number for contract {selectedContract?.contract_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>PO Number *</Label>
              <Input
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                placeholder="Enter PO number"
                data-testid="po-number-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPoDialogOpen(false)} data-testid="cancel-po-button">
                Cancel
              </Button>
              <Button onClick={handleUpdatePO} className="bg-accent hover:bg-accent/90" data-testid="submit-po-button">
                Update PO
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Client PO Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Client Purchase Order</DialogTitle>
            <DialogDescription>
              Upload the client's PO document for {selectedContract?.contract_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="border-2 border-dashed rounded-lg p-8 text-center">
              {selectedFile ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="h-8 w-8 text-accent" />
                  <div className="text-left">
                    <p className="font-semibold">{selectedFile.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setSelectedFile(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <FileUp className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground mb-3">
                    Drag & drop or click to upload
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Supported: PDF, JPG, PNG, DOCX (max 10MB)
                  </p>
                </>
              )}
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.docx"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                data-testid="file-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setUploadDialogOpen(false);
                  setSelectedFile(null);
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleFileUpload} 
                disabled={!selectedFile || uploadingFile}
                className="bg-accent hover:bg-accent/90"
                data-testid="upload-file-button"
              >
                {uploadingFile ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mobilize Dialog */}
      <Dialog open={mobilizeDialogOpen} onOpenChange={setMobilizeDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Mobilize Contract - Assign Assets</DialogTitle>
            <DialogDescription>
              Select available assets to assign to {selectedContract?.contract_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {assets.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Truck className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No available assets found</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {assets.map((asset) => (
                  <div
                    key={asset.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-all ${
                      selectedAssets.includes(asset.id) ? 'border-accent bg-accent/5' : 'hover:border-accent/50'
                    }`}
                    onClick={() => toggleAsset(asset.id)}
                    data-testid={`asset-option-${asset.id}`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedAssets.includes(asset.id)}
                        onChange={() => {}}
                        className="h-4 w-4"
                      />
                      <div className="flex-1">
                        <div className="font-semibold">{asset.make} {asset.model}</div>
                        <div className="text-sm text-muted-foreground">{asset.registration_number}</div>
                        <div className="text-xs text-muted-foreground">{asset.asset_type}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-between items-center pt-4 border-t">
              <div className="text-sm text-muted-foreground">
                {selectedAssets.length} asset(s) selected
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setMobilizeDialogOpen(false)} data-testid="cancel-mobilize-button">
                  Cancel
                </Button>
                <Button 
                  onClick={handleMobilize} 
                  className="bg-green-600 hover:bg-green-700" 
                  disabled={selectedAssets.length === 0}
                  data-testid="confirm-mobilize-button"
                >
                  Mobilize Contract
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
